/**
 * VRG-545 — the "Potential brokerage ARR" column.
 *
 * ## ⚠️ A DERIVED COLUMN, DELIBERATELY NOT IN `WEBSITE_FIELDS`
 *
 * Same reasoning as `potentialRevenueColumn.tsx`, and it applies with equal force here:
 * `WEBSITE_FIELDS` is THE CONTRACT WITH vera-website, and
 * `signups-website-field-coverage.guard.spec.ts` asserts it against that repo's own
 * `content/roi.ts`. A manifest entry for a figure the site has never sent would make the
 * guard assert a field that does not exist — so it either fails on a correct manifest, or
 * gets loosened until it no longer catches real drift. A forwarded field and a computed one
 * are different claims (working-conventions §76).
 *
 * ## The currency here is the ROW's, and its neighbour's is not
 *
 * ⚠️ THE TWO REVENUE COLUMNS ON THIS GRID ARE IN DIFFERENT CURRENCIES ON PURPOSE. This one
 * is derived from the visitor's own `price`, so it carries `roi_submission.currency`;
 * subscription ARR is derived from Vera's USD catalog, so it carries USD. Each inherits the
 * currency of what it is built from, which is the only labelling that is true of both.
 *
 * It will look like a bug to the next reader on a euro row — two adjacent money columns,
 * `€` and `$` — so both tooltips name their currency explicitly, and `brokerageRevenue.ts`
 * carries the full reasoning. Nothing converts; VRG-577 is the conversion subsystem.
 */
import React from 'react'

import { Box, Stack, Typography } from '@mui/material'
import { EMPTY_VALUE, Tooltip } from '@repo/ui'

import { headerWithInfo, INFO_HEADER_ALLOWANCE } from 'components'
import type { SignupEntry } from 'hooks/api/useSignups'

import { brokerageRevenue, BROKERAGE_FEE_PERCENT, type BrokerageRevenue } from './brokerageRevenue'
import { currencySymbolOf } from './websiteValues'

const cellSx = { display: 'flex', alignItems: 'center', height: '100%', minWidth: 0 }

/**
 * The label, once — `headerName` feeds the column menu, the Layout visibility panel and the
 * CSV export; `renderHeader` feeds the rendered header. Two literals is a half-done rename.
 */
const COLUMN_LABEL = 'Potential brokerage ARR'

/**
 * §49 — sized for the HEADER, not the values, and MEASURED rather than estimated.
 *
 * A money column's values are short and the instinct is to give it ~140px, which is exactly
 * the mistake §49 records (`Inve…`, `In sto…`).
 *
 * ⚠️ MEASURED IN THE BROWSER on `/internal/signups` at 1280×800, by walking the rendered
 * header cell. Same decomposition as `potentialRevenueColumn`, which carries the full
 * working and the reason the chrome is 74 rather than the 63 the manifest quotes (the extra
 * is the 28px sort-icon container, which only a `sortable` column reserves):
 *
 *     label text ("Potential brokerage ARR", 14px Lato)  152
 *     chrome (20 cell pad + 8 title pad + 28 sort + 18 info glyph)  74
 *     ──────────────────────────────────────────────────────
 *     minimum                                            226   → column 243, 17px slack
 */
const HEADER_WIDTH = 225

/**
 * ⚠️ THE ROW'S CURRENCY, not a `'$'` literal and not the catalog's. Through the shared
 * resolver so an unknown code prints as the code rather than borrowing a glyph.
 *
 * ⚠️ `fractionDigits` is 2 FOR THE PER-ITEM RESALE PRICE and 0 for the totals, and that is
 * not cosmetic. The tooltip exists to be checked by hand, so its factors have to multiply
 * to its product. Rounding every figure to whole units printed "9,600 items at €563 each =
 * €5,400,000" — and 9,600 × 563 is €5,404,800. The stated working contradicted its own
 * total, on a breakdown whose entire job is letting a reviewer redo the arithmetic. A unit
 * price is where the cents live (562.50); a five-figure annual total is not.
 */
const money = (row: SignupEntry, value: number, fractionDigits = 0): string =>
  `${currencySymbolOf(row.roi_submission)}${value.toLocaleString('en-US', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })}`

/**
 * What the cell says on hover — the arithmetic, shown rather than asserted.
 *
 * A single large number under "ARR" is the most misreadable thing that can sit on a
 * lead-qualification screen: it looks like a booking and is a fee calculation on four
 * figures a stranger typed. Naming the resale volume, the per-item resale price, the market
 * they multiply to, and Vera's cut of it is what lets a reviewer decide how much to believe
 * — and, specifically, lets them see that the 2.5% is levied on the RESALE VALUE rather
 * than on the brand's own share of it.
 */
const breakdown = (row: SignupEntry, r: BrokerageRevenue): string => {
  const n = (value: number) => Math.round(value).toLocaleString('en-US')
  return [
    `${n(r.unitsResold)} items resold a year (${(r.resaleRate * 100).toLocaleString('en-US', { maximumFractionDigits: 4 })}% of their stated volume)`,
    `at ${money(row, r.resaleUnitValue, 2)} each (${(r.resaleValueRatio * 100).toLocaleString('en-US', { maximumFractionDigits: 4 })}% of their retail price)`,
    `= ${money(row, r.resaleGmv)} of resale value.`,
    `Vera takes ${BROKERAGE_FEE_PERCENT}% of that from the seller.`,
    `In the currency the visitor entered their own figures in. That is not the currency of the subscription ARR column, which is Vera's USD price list.`,
  ].join(' ')
}

/*
  §15 — no em-dash overuse in UI copy. This is a header tooltip a reviewer reads while
  deciding whether to trust a number, so the clauses are separate sentences rather than
  dash-joined ones.
*/
const HEADER_NOTE =
  `What Vera would earn a year brokering this lead's resales. Their stated annual volume, ` +
  `the share of it they expect to be resold, and what a resold item fetches, multiplied out ` +
  `to a resale value. Vera takes ${BROKERAGE_FEE_PERCENT}% of that from the seller. ` +
  "This is Vera's fee, not the brand's own share of a resale. That is the separate " +
  '"Your share of a resale" column, and it is not an input here. ' +
  "Blank unless all four figures are the visitor's own rather than the site's seeded " +
  'defaults, because a revenue figure built partly from our starting values would be ours, not theirs. ' +
  "In the row's own currency, unlike Potential subscription ARR, which is always in USD."

export const buildBrokerageRevenueColumn = () => ({
  field: 'brokerage_revenue',
  headerName: COLUMN_LABEL,
  type: 'number' as const,
  width: HEADER_WIDTH + INFO_HEADER_ALLOWANCE,
  sortable: true,
  filterable: true,
  renderHeader: headerWithInfo(COLUMN_LABEL, HEADER_NOTE),
  /*
    MUI X v7 signature `(value, row, column, apiRef)`. There is no `row.brokerage_revenue`
    to read — the whole column is computed — so the row is the only input, and this is what
    the CSV export and the sort/filter engines consult. `renderCell` formats what this
    returns and never recomputes it, so a sort cannot disagree with what is on screen.

    ⚠️ Returns `null`, not 0, for a row that cannot be priced. A 0 is a revenue figure and
    would sort among the real ones as the cheapest lead on the page — and here it would
    also be indistinguishable from a lead who genuinely answered "nothing is resold".

    ⚠️ AND THE SORT MIXES CURRENCIES. Sorting this column ranks a €-row's number against a
    $-row's as bare magnitudes, because nothing converts. That is the same limitation the
    `price`, `cac` and `total` columns already carry (see `RoiSubmission.currency`: do not
    sum or average across rows without grouping on it) — stated here rather than fixed,
    since a conversion with no rate and no rate date states a number nobody computed.
  */
  valueGetter: (_value: unknown, row: SignupEntry) =>
    row ? brokerageRevenue(row)?.total ?? null : null,
  renderCell: ({ row }: { row: SignupEntry }) => {
    const revenue = row ? brokerageRevenue(row) : null
    if (!revenue) {
      return (
        <Box sx={cellSx}>
          <Typography variant="body2" color="text.disabled">
            {EMPTY_VALUE}
          </Typography>
        </Box>
      )
    }
    return (
      <Tooltip title={breakdown(row, revenue)}>
        <Stack direction="row" spacing={2} alignItems="center" sx={cellSx}>
          <Typography variant="body2" noWrap>
            {money(row, revenue.total)}
          </Typography>
        </Stack>
      </Tooltip>
    )
  },
})
